import React, { useState, useRef, useEffect } from 'react';
import { getUserFriendlyError } from '../utils/errorMessages';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { chatAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Send, Paperclip, Mic, Image, FileText, ArrowLeft, Loader2, MessageSquare } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

/**
 * 💬 YourSpace - General Purpose AI Chat
 * 
 * This is a ChatGPT-style general conversation interface:
 * - Independent of Allora's personal memory/context
 * - For general questions and AI-assisted discussions
 * - Uses a separate session from Allora chat
 */
const YourSpace = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, locale } = useLocale();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    // Load chat history from backend - using 'general' session (not 'allora')
    const loadMessages = async () => {
      try {
        const response = await chatAPI.getMessages('general');
        const history = response.data || [];
        
        // If no chat history, show welcome message for general chat
        if (history.length === 0) {
          setMessages([{
            id: 'welcome',
            role: 'assistant',
            content: t('yourspace.welcome') || `Welcome to YourSpace! I'm here to help with any questions or discussions. This is your private space for general AI conversations - separate from your personal Allora chats. What can I help you with?`,
            timestamp: new Date().toISOString()
          }]);
        } else {
          setMessages(history);
        }
      } catch (error) {
        console.error('Error loading messages:', error);
        // Show welcome even on error
        setMessages([{
          id: 'welcome',
          role: 'assistant',
          content: t('yourspace.welcome') || `Welcome to YourSpace! I'm here to help with any questions or discussions. What can I help you with?`,
          timestamp: new Date().toISOString()
        }]);
      } finally {
        setInitialLoading(false);
      }
    };

    loadMessages();
  }, [t]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() && !pendingFile) return;

    // Build user message content
    let displayContent = input.trim();
    if (pendingFile && pendingFilePreview) {
      displayContent = input.replace(`[Attached: ${pendingFile.name}] `, '').trim() || `What's in this image?`;
    }

    const userMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: displayContent,
      image: pendingFilePreview, // Include image preview if exists
      fileName: pendingFile?.name,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    
    const userInput = input.replace(`[Attached: ${pendingFile?.name}] `, '').trim() || 'Describe this image';
    const currentFile = pendingFile;
    
    setInput('');
    setPendingFile(null);
    setPendingFilePreview(null);
    setLoading(true);

    try {
      let response;
      
      if (currentFile) {
        // Send file with message
        const formData = new FormData();
        formData.append('file', currentFile);
        formData.append('message', userInput);
        
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/chat/with-file`,
          {
            method: 'POST',
            credentials: 'include',
            body: formData
          }
        );
        
        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.detail?.message || error.detail || 'Failed to process');
        }
        
        response = { data: await res.json() };
      } else {
        // Regular text message - pass user's language preference
        response = await chatAPI.sendMessage(userInput, 'default', locale?.language || 'en');
      }
      
      const aiMessage = {
        id: response.data.message_id || `msg-${Date.now()}`,
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to get AI response. Please try again.',
        variant: 'destructive'
      });
      
      const errorMessage = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: "Sorry, I'm having a moment. Could you try that again? I'm here for you.",
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const [pendingFile, setPendingFile] = useState(null);
  const [pendingFilePreview, setPendingFilePreview] = useState(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please upload a file smaller than 10MB.",
        variant: "destructive"
      });
      return;
    }
    
    // Store file for later use with message
    setPendingFile(file);
    
    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPendingFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPendingFilePreview(null);
    }
    
    // Set input to prompt user
    setInput(`[Attached: ${file.name}] `);
    
    toast({
      title: "File attached 📎",
      description: "Type a message or question about it, then send."
    });
    
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (imageInputRef.current) imageInputRef.current.value = '';
  };
  
  const clearPendingFile = () => {
    setPendingFile(null);
    setPendingFilePreview(null);
    setInput('');
  };

  // Voice recording functions
  const startRecording = async () => {
    // Check if browser supports getUserMedia
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('getUserMedia not supported');
      toast({
        title: "Voice not supported",
        description: "Your browser doesn't support voice input. Try Chrome or Safari.",
        variant: "destructive"
      });
      return;
    }
    
    try {
      console.log('🎤 Requesting microphone access...');
      
      // Fetch voice configuration from backend for optimal settings
      let voiceConfig = null;
      try {
        const configResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/ai/voice-config`,
          { credentials: 'include' }
        );
        if (configResponse.ok) {
          voiceConfig = await configResponse.json();
          console.log('Voice config from backend:', voiceConfig);
        }
      } catch (configError) {
        console.log('Could not fetch voice config, using defaults');
      }
      
      // Request permission explicitly first on mobile
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const permStatus = await navigator.permissions.query({ name: 'microphone' });
          console.log('Microphone permission status:', permStatus.state);
        } catch (e) {
          console.log('Permission query not supported, continuing with getUserMedia');
        }
      }
      
      // Use constraints from backend config if available
      const audioConstraints = voiceConfig?.constraints?.audio || {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      };
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: audioConstraints
      });
      console.log('✅ Microphone access granted');
      
      // Check for supported MIME types - prioritize backend recommendations
      let mimeType = '';
      const mimeTypes = voiceConfig?.mime_types_to_try || 
        ['audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/wav', 'audio/aac'];
      
      for (const type of mimeTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }
      console.log('Using MIME type:', mimeType || 'default');
      
      // Show iOS-specific guidance if needed
      if (voiceConfig?.is_ios && !voiceConfig?.voice_supported) {
        toast({
          title: voiceConfig.guidance?.title || "Voice Input Limited",
          description: voiceConfig.guidance?.alternative || "Voice may not work in this browser. Try Safari.",
          variant: "default"
        });
      }
      
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const finalMimeType = mediaRecorder.mimeType || mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: finalMimeType });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      // Use timeslice from config if available (more frequent on mobile)
      const timeslice = voiceConfig?.recording_options?.timeslice || 100;
      mediaRecorder.start(timeslice);
      setIsRecording(true);
      toast({ title: "🎤 Recording...", description: "Tap again to stop" });
    } catch (error) {
      console.error('❌ Error starting recording:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      
      let errorMessage = "Please allow microphone access to use voice input.";
      if (error.name === 'NotAllowedError') {
        errorMessage = "Microphone access was denied. Please check your browser settings and allow microphone access for this site.";
      } else if (error.name === 'NotFoundError') {
        errorMessage = "No microphone found. Please connect a microphone.";
      } else if (error.name === 'NotSupportedError' || error.name === 'NotReadableError') {
        errorMessage = "Voice recording is not available. The microphone may be in use by another app.";
      } else if (error.name === 'SecurityError') {
        errorMessage = "Microphone access blocked. Make sure you're using HTTPS.";
      }
      
      toast({
        title: "Microphone error",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setLoading(true);
    try {
      const formData = new FormData();
      
      // Determine file extension based on blob type
      const mimeToExt = {
        'audio/webm': 'webm',
        'audio/mp4': 'mp4',
        'audio/mpeg': 'mp3',
        'audio/wav': 'wav',
        'audio/m4a': 'm4a'
      };
      const ext = mimeToExt[audioBlob.type] || 'webm';
      
      formData.append('file', audioBlob, `recording.${ext}`);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/transcribe`,
        {
          method: 'POST',
          credentials: 'include',
          body: formData
        }
      );

      if (!response.ok) {
        throw new Error('Transcription failed');
      }

      const data = await response.json();
      if (data.text) {
        setInput(data.text);
        toast({ title: "✨ Transcribed!", description: "You can edit or send" });
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast({
        title: "Transcription failed",
        description: "Please try again or type your message.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMicClick = (e) => {
    // Prevent default to ensure touch events work properly on mobile
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🎙️ Mic button clicked, isRecording:', isRecording);
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const quickActions = [
    { text: 'How was my day?', icon: FileText },
    { text: 'What should I focus on?', icon: FileText },
    { text: 'Help me feel better', icon: FileText },
    { text: 'I need to talk', icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-teal-50 to-white flex flex-col">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/home')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Your Space</h1>
                <p className="text-xs text-gray-500">Ask me anything</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Chat Area */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 overflow-y-auto">
        {/* Quick Actions (shown when no messages) */}
        {messages.length === 0 && (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-gray-900">Welcome to Your Space</h2>
              <p className="text-gray-600">Your universal AI assistant for Work, Life, and Health</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {quickActions.map((action, index) => (
                <Card
                  key={index}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setInput(action.text)}
                >
                  <CardContent className="p-4 flex items-center space-x-3">
                    <action.icon className="h-5 w-5 text-blue-600" />
                    <p className="text-sm font-medium text-gray-700">{action.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-gradient-to-r from-blue-600 to-teal-600 text-white'
                    : 'bg-white text-gray-900 shadow-sm border border-gray-200'
                }`}
              >
                {/* Show image if attached */}
                {message.image && (
                  <img 
                    src={message.image} 
                    alt={message.fileName || 'Uploaded'} 
                    className="max-w-full max-h-48 rounded-lg mb-2 object-contain"
                  />
                )}
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                <p
                  className={`text-xs mt-1 ${
                    message.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                  }`}
                >
                  {new Date(message.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="bg-white rounded-2xl px-4 py-3 shadow-sm border border-gray-200">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input Area */}
      <div className="bg-white border-t border-gray-200 sticky bottom-0">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* File Preview */}
          {pendingFile && (
            <div className="mb-3 flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              {pendingFilePreview ? (
                <img src={pendingFilePreview} alt="Preview" className="h-16 w-16 object-cover rounded" />
              ) : (
                <div className="h-16 w-16 bg-gray-200 rounded flex items-center justify-center">
                  <FileText className="h-6 w-6 text-gray-500" />
                </div>
              )}
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700">{pendingFile.name}</p>
                <p className="text-xs text-gray-500">Type a message about this file</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearPendingFile}
                className="text-gray-500 hover:text-red-500"
              >
                ✕
              </Button>
            </div>
          )}
          <form onSubmit={handleSendMessage} className="flex items-end space-x-2">
            <div className="flex space-x-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                className="text-gray-600 hover:text-gray-900"
              >
                <Paperclip className="h-5 w-5" />
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.mp3,.wav"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => imageInputRef.current?.click()}
                className="text-gray-600 hover:text-gray-900"
              >
                <Image className="h-5 w-5" />
              </Button>
              <input
                ref={imageInputRef}
                type="file"
                className="hidden"
                onChange={handleFileUpload}
                accept="image/*"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleMicClick}
                onTouchEnd={handleMicClick}
                className={`${isRecording ? 'text-red-500 animate-pulse' : 'text-gray-600 hover:text-gray-900'} touch-manipulation`}
              >
                <Mic className="h-5 w-5" />
              </Button>
            </div>

            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 h-11"
              disabled={loading}
            />

            <Button
              type="submit"
              disabled={!input.trim() || loading}
              className="h-11 px-4 bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
            >
              <Send className="h-5 w-5" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default YourSpace;
